import { useMemo, useState } from 'react';

import { type ColumnDef } from '@tanstack/react-table';
import { Trash2Icon } from 'lucide-react';

import { Button, FormCounter } from '@/_components';
import { CellName, CellText, ColumnHeader, RowDragHandleCell } from '@/_containers';

import type { PickTicketsItemFormSchema } from '../../../pick-ticket-items-schemas';

export type HandleDelete = ((id: number) => void) | undefined;

export function useDefineColumns(
  isEditable: boolean,
  onCellBlur: (index: number, id: number, name: keyof PickTicketsItemFormSchema, v: number) => void,
  handleDelete?: HandleDelete
) {
  const [oID, setOID] = useState<null | number>(null);

  return useMemo(() => {
    const columns: ColumnDef<PickTicketsItemFormSchema>[] = [
      {
        id: 'sort_order',
        meta: { name: '', className: `pl-2 pr-0 w-14 max-w-14 ${isEditable ? '' : 'hidden'}` },
        header: ({ column }) => <ColumnHeader column={column} />,
        cell: () => <RowDragHandleCell />,
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        accessorFn: (row) => ({
          id: row?.inventory?.id,
          name: row?.inventory?.name ?? '—',
          primary_sku: row?.inventory?.primary_sku ?? '—',
        }),
        meta: { name: 'Inventory', className: 'w-full max-w-64' },
        header: ({ column }) => <ColumnHeader column={column} />,
        cell: ({ row }) => {
          const { id, name, primary_sku } = row.getValue('name') as { id: number; name: string; primary_sku: string };
          return (
            <CellName
              to="/inventory/$id"
              params={{ id: `${id}` }}
              className="overflow-visible! text-clip! whitespace-normal!"
              showAvatar={false}
              name={`${primary_sku} - ${name}`}
            />
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'available_quantity',
        accessorFn: (row) => ({ current_stock: row.inventory?.current_stock ?? 0 }),
        meta: {
          name: 'Available',
          headerClassName: 'align-middle w-32 min-w-32 max-w-32',
          className: `px-2 ${isEditable ? 'py-0' : ''}`,
        },
        header: ({ column }) => <ColumnHeader column={column} align="center" />,
        cell: ({ row }) => {
          const { getValue } = row;
          const { current_stock } = getValue('available_quantity') as { current_stock: number };
          return <CellText align="center">{current_stock}</CellText>;
        },
        enableSorting: false,
      },

      {
        accessorKey: 'quantity',
        accessorFn: (row, index) => ({
          id: row.id,
          quantity: row.quantity,
          index,
          current_stock: row.inventory?.current_stock ?? 0,
        }),
        meta: {
          name: 'Quantity',
          headerClassName: 'align-middle w-32 min-w-32 max-w-32',
          className: `px-2 ${isEditable ? 'py-0 align-bottom' : ''}`,
        },
        header: ({ column }) => <ColumnHeader column={column} align="center" />,
        cell: ({ row }) => {
          const { getValue } = row;
          const { quantity, id, index, current_stock } = getValue('quantity') as {
            current_stock: number;
            quantity: number;
            id: number;
            index: number;
          };
          // TODO: Fix quantity value
          if (isEditable) {
            return (
              <div className="-mb-px">
                <FormCounter
                  hideMsg
                  count={quantity ?? 0}
                  min={1}
                  max={current_stock}
                  onCountChange={(value) => {
                    onCellBlur(index, id, 'quantity', value ?? 0);
                    return true;
                  }}
                />
              </div>
            );
          }

          return <CellText align="center">{quantity}</CellText>;
        },
        enableSorting: false,
      },
    ];

    if (isEditable && handleDelete) {
      columns.push({
        id: 'actions',
        meta: { name: '', className: 'w-10 px-2 pl-1 min-w-10 max-w-10 align-bottom' },
        accessorFn: (row) => row?.id,
        header: ({ column }) => <ColumnHeader column={column} />,
        cell: ({ row }) => {
          const dId = row.getValue('actions') as number;
          return (
            <div className="flex items-end justify-end">
              <Button
                size="xs"
                as="link"
                showLoader={oID === dId}
                disabled={oID !== null && oID !== dId}
                aria-label="Delete inventory item"
                onClick={async () => {
                  setOID(dId);
                  await handleDelete(dId);
                  setOID(null);
                }}
                icon={Trash2Icon}
              />
            </div>
          );
        },
        enableSorting: false,
      });
    }

    return columns;
  }, [isEditable, onCellBlur, handleDelete, oID]);
}
